import { Link } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import { List, MagnifyingGlass, Gear } from "@phosphor-icons/react";
// If you already use react-i18next:
import { useTranslation } from "react-i18next";
// Keep your components; ensure alias '@' points to '/src' in Vite
import LocaleSwitcher from "../components/locale/LocaleSwitcher";
import ThemeToggle from "../components/theme/ThemeToggle";

export default function Topbar({ onMenu }: { onMenu: () => void }) {
  const { dir } = useTheme() as { dir: "rtl" | "ltr" };
  const { t } = useTranslation(); // falls back if not initialized yet

  return (
    <Bar>
      <Left>
        <MenuButton onClick={onMenu} aria-label="Open menu">
          <List size={22} weight="bold" />
        </MenuButton>
      </Left>

      <Center>
        <Brand to="/">
          <Logo>
            <img
              src="/logo.png"
              alt={t("app.title", { defaultValue: "Quran" }) as string}
            />
          </Logo>
        </Brand>
      </Center>

      <Right>
        <SearchBox role="search">
          <MagnifyingGlass size={18} />
          <input
            placeholder={dir === "rtl" ? "ابحث..." : "Search…"}
            aria-label={dir === "rtl" ? "ابحث" : "Search"}
          />
        </SearchBox>

        <ThemeToggle />
        <LocaleSwitcher />

        <IconButton as={Link} to="/settings" aria-label="Settings">
          <Gear size={20} />
        </IconButton>
      </Right>
    </Bar>
  );
}

const Bar = styled.header`
  position: sticky; top: 0; z-index: 40; height: 64px;
  display: grid; grid-template-columns: 1fr auto 1fr; align-items: center;
  padding-inline: clamp(16px, 3vw, 24px);
  background: color-mix(in oklab, var(--surface) 85%, transparent);
  backdrop-filter: blur(16px) saturate(180%);
  border-bottom: 1px solid var(--border);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02);
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, 
      transparent 0%, 
      color-mix(in oklab, var(--primary) 30%, transparent) 50%, 
      transparent 100%
    );
    opacity: 0.4;
  }
`;

const Left = styled.div` display: flex; align-items: center; `;
const Center = styled.div` display: flex; align-items: center; justify-content: center; `;
const Right = styled.div` display: flex; align-items: center; justify-content: flex-end; gap: 10px; `;

const BaseButton = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  height: 40px; padding-inline: 12px; border-radius: 12px;
  border: 1px solid var(--border); 
  background: var(--surface); 
  color: var(--text);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  
  &:hover {
    background: color-mix(in oklab, var(--surface) 95%, var(--text));
    border-color: color-mix(in oklab, var(--border) 70%, var(--text));
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }
`;

const MenuButton = styled(BaseButton)`
  width: 40px; padding-inline: 0;
  @media (min-width: 1000px) { display: none; }
`;

const IconButton = styled(BaseButton)` width: 40px; padding-inline: 0; `;

const Brand = styled(Link)` 
  display: inline-flex; 
  align-items: center; 
  text-decoration: none; 
  transition: transform 0.2s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const Logo = styled.div`
  display: flex; align-items: center; justify-content: center;
  img { 
    display: block; 
    height: 48px; 
    width: auto; 
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.08));
  }
`;

const SearchBox = styled.label`
  display: none;
  @media (min-width: 640px) { display: inline-flex; }
  align-items: center; gap: 10px; height: 40px; min-width: 240px;
  border-radius: 12px; padding-inline: 14px; 
  background: color-mix(in oklab, var(--background) 50%, var(--surface));
  border: 1.5px solid var(--border); 
  color: var(--text);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: all 0.25s ease;
  cursor: text;
  
  &:focus-within {
    border-color: var(--primary);
    background: var(--surface);
    box-shadow: 0 0 0 3px color-mix(in oklab, var(--primary) 15%, transparent),
                inset 0 1px 2px rgba(0, 0, 0, 0.02);
  }
  
  svg { 
    opacity: .7; 
    transition: opacity 0.2s ease;
  }
  
  &:focus-within svg {
    opacity: 1;
    color: var(--primary);
  }
  
  input {
    outline: none; 
    border: 0; 
    background: transparent; 
    width: 200px; 
    color: var(--text);
    font-size: 0.95rem;
  }
  input::placeholder { color: var(--muted); opacity: 0.7; }
`;
